/**
 * UpgradeBlock — overlay/banner shown when the user has not subscribed to
 * the cert they're viewing. Drives users to the $14.99/mo per-cert Stripe
 * link with their Cognito sub AND the cert code encoded in
 * client_reference_id so the webhook knows which cert was paid for.
 *
 * client_reference_id format: '<cognito-sub>__<CERT-CODE>'
 *   - delimiter is double underscore so Stripe's allowed character set
 *     ([A-Za-z0-9_-]) doesn't conflict with cert hyphens
 *   - empty cert code falls back to the legacy all-access bundle path
 *
 * The 'compact' variant renders an inline pill on locked cert cards.
 * Both variants stop click propagation so they don't fire the parent
 * cert-card onClick.
 */
import React, { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { TIER_LABELS } from './certTiers';
import './UpgradeBlock.css';

const EXAM_STRIPE_BASE = 'https://buy.stripe.com/3cI6oGbkn60YekxeOH5gc0g';
const PLATFORM_PRICING_URL =
  'https://dreamandbuildcloudai.com/?utm_source=exam-app&utm_campaign=cross_sell#pricing';

/**
 * Build the Stripe URL with both the Cognito sub and the cert code encoded
 * in client_reference_id. Falls back to bare URL if no sub is available.
 */
function buildStripeUrl(sub, certCode) {
  if (!sub) return EXAM_STRIPE_BASE;
  const sep = EXAM_STRIPE_BASE.includes('?') ? '&' : '?';
  const ref = certCode
    ? `${sub}__${String(certCode).toUpperCase()}`
    : sub;
  return `${EXAM_STRIPE_BASE}${sep}client_reference_id=${encodeURIComponent(ref)}`;
}

/**
 * UpgradeBlock props:
 *   requiredTier  — used only for the label copy ('Student' / 'Professional')
 *   compact       — render the inline pill instead of the full block
 *   certCode      — REQUIRED for per-cert subs; encoded into client_reference_id
 *   certName      — optional; used in the full-block subhead for clarity
 */
export default function UpgradeBlock({ requiredTier, compact, certCode, certName }) {
  const [stripeUrl, setStripeUrl] = useState(() => buildStripeUrl('', certCode));

  useEffect(() => {
    let cancelled = false;
    async function loadSub() {
      try {
        const session = await fetchAuthSession().catch(() => null);
        const sub = session?.tokens?.idToken?.payload?.sub || '';
        if (!cancelled) setStripeUrl(buildStripeUrl(sub, certCode));
      } catch {
        /* fall back to bare URL */
      }
    }
    loadSub();
    return () => { cancelled = true; };
  }, [certCode]);

  if (compact) {
    return (
      <a
        href={stripeUrl}
        className="upgrade-pill"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={
          certCode
            ? `Subscribe $14.99/mo to unlock ${certCode}`
            : `Subscribe $14.99/mo to unlock ${TIER_LABELS[requiredTier]} certs`
        }
        onClick={(e) => e.stopPropagation()}
      >
        Subscribe — $14.99/mo
      </a>
    );
  }
  return (
    <div className="upgrade-block" role="status">
      <div className="upgrade-block-icon" aria-hidden="true">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          focusable="false"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
        </svg>
      </div>
      <div className="upgrade-block-copy">
        <h3 className="upgrade-block-title">
          Unlock {certCode || 'this cert'} — $14.99/month
        </h3>
        <p className="upgrade-block-body">
          {certName ? `${certName} ` : ''}
          {certCode ? `(${certCode}) ` : ''}
          is part of the per-cert exam subscription. $14.99/month unlocks
          the full question bank, practice quizzes, and timed exams for
          this certification. Cancel anytime from your Stripe portal.
        </p>
        <a
          href={stripeUrl}
          className="upgrade-block-cta"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          Subscribe — $14.99/mo →
        </a>
        <p className="upgrade-block-secondary">
          Want labs, capstones, jobs board, and quizzes across every cert?{' '}
          <a
            href={PLATFORM_PRICING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="upgrade-block-secondary-link"
            onClick={(e) => e.stopPropagation()}
          >
            See the D&amp;B Cloud Learning Platform plans →
          </a>
        </p>
      </div>
    </div>
  );
}
